// GoogleButton.js — Botão "Continuar com o Google".
//
// Usa o Google Identity Services (GIS): o próprio Google renderiza o botão,
// que é o padrão visual que todo site usa (Notion, Figma, Canva…) e o único
// jeito de ficar 100% dentro das diretrizes de marca do Google.
//
// O botão devolve um "credential" (ID token JWT). Ele vai para o nosso backend,
// que valida com o Google antes de emitir o token da Insigne — o navegador
// nunca decide sozinho quem está logado.
//
// Sem REACT_APP_GOOGLE_CLIENT_ID configurado, renderizamos uma RÉPLICA visual
// desativada, para o layout da página continuar correto durante o setup.
import React, { useEffect, useRef, useState } from "react";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SRC = "https://accounts.google.com/gsi/client";

// Sem client_id o login social não existe. Em produção o bloco inteiro some
// (nada de botão morto para o cliente); em desenvolvimento mostramos uma
// réplica desativada para conferir o layout durante o setup.
export const googleConfigurado = !!CLIENT_ID;
export const mostrarBlocoGoogle = googleConfigurado || process.env.NODE_ENV === "development";

// Carrega o script do Google uma única vez, mesmo com dois botões na tela.
let gsiPromise = null;
const carregarGsi = () => {
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar o Google."));
    document.head.appendChild(script);
  });
  return gsiPromise;
};

// SVG oficial do "G" — usado só na réplica desativada.
const LogoG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const GoogleButton = ({ onCredential, onError, texto = "signin_with" }) => {
  const containerRef = useRef(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelado = false;

    carregarGsi()
      .then(() => {
        if (cancelado || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resposta) => {
            if (resposta?.credential) onCredential(resposta.credential);
            else onError?.("Não foi possível obter a resposta do Google.");
          },
          // popup evita que o site seja recarregado no meio do checkout
          ux_mode: "popup",
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: texto, // signin_with = "Fazer login com o Google" | signup_with = "Inscrever-se com o Google"
          shape: "rectangular",
          logo_alignment: "left",
          locale: "pt-BR",
          // A LARGURA DO PRÓPRIO CARTÃO, até 360 (18/10/2026). Com 360 fixo, no
          // celular o botão passava da borda do cartão e alargava a página
          // inteira (421px numa tela de 390). O Google aceita de 200 a 400.
          width: Math.max(200, Math.min(360, Math.floor(containerRef.current.offsetWidth || 360))),
        });
      })
      .catch(() => {
        if (!cancelado) setFalhou(true);
      });

    return () => {
      cancelado = true;
    };
  }, [onCredential, onError, texto]);

  // Sem client_id (ou script bloqueado): réplica visual desativada.
  if (!CLIENT_ID || falhou) {
    return (
      <div>
        <button
          type="button"
          disabled
          className="w-full flex items-center justify-center gap-3 rounded-lg bg-paper border border-[#dadce0]
                     px-4 py-3 text-sm font-medium text-ink-fixed opacity-60 cursor-not-allowed"
        >
          <LogoG />
          Continuar com o Google
        </button>
        <p className="text-xs text-white/40 mt-2">
          {falhou
            ? "Não foi possível carregar o Google agora."
            : "Login com Google ainda não configurado neste ambiente."}
        </p>
      </div>
    );
  }

  // O Google injeta o botão real aqui dentro.
  return <div ref={containerRef} className="flex justify-center [&>div]:!w-full" />;
};

export default GoogleButton;
